/**
 * @lines98/client/components/achievement-detail — ЕДИНЫЙ модуль детали ачивки:
 * компактный АНКЕР-ПОПАП у нажатого значка (название + «как получить» + награда
 * мелкими иконками + статус цветом рамки/галочкой).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (тикет fe-achievement-detail-popover.md R-2 + owner-playtest
 * 2026-06-21): раньше деталь ачивки была ЦЕНТРИРОВАННОЙ модалкой шириной в доску с
 * крупными иконками (`buildDetailDialog` ДУБЛИРОВАЛСЯ байт-в-байт в `badge-strip.ts` и
 * `badge-grid.ts`) — owner: «огромная модалка в углу с огромным медальоном и осколками».
 * Owner-decisions: окошко ВПЛОТНУЮ к значку с хвостиком, тумблер по тому же значку,
 * компактный контент, мелкие награды. Этот модуль — ОДИН источник детали для обоих
 * потребителей (badge-strip грид Профиля + badge-grid), на анкер-стволе
 * `lib/anchored-popover` (R-1). Дубля больше нет.
 *
 * SCOPE: наполнение анкер-попапа контентом ачивки (имя/описание/награда/статус) +
 *   tap-toggle с внешнего значка-триггера. NOT IN SCOPE: анкер-механика/флип/хвостик/
 *   тумблер (ствол `lib/anchored-popover`), вид значка-грида (badge-strip/badge-grid CSS),
 *   данные каталога (lib/achievement-catalog), состав награды (@lines98/shared ACH_REWARD).
 *
 * Класс `<dialog>` = `badge-detail-dialog` (+ анкер-каркас `anchored-popover`): вид
 * (компактные размеры/мелкие награды/рамка-статус) — co-located `achievement-detail.css`.
 *
 * Cross-references:
 *   - tickets/fe-achievement-detail-popover.md (AC-1..AC-19).
 *   - lib/anchored-popover (анкер-ствол: флип/хвостик/тумблер/registry).
 *   - lib/achievement-catalog (ACHIEVEMENT_DISPLAY — имя/описание).
 *   - @lines98/shared/config/meta (ACH_REWARD — золото/осколки по тиру).
 *   - components/meta-icons (ICON_GOLD/ICON_SHARD — .meta-glyph, компактно через CSS).
 */

import './achievement-detail.css';

import { ACH_REWARD, type AchievementRarity } from '@lines98/shared/config/meta';

import { createAnchoredPopover, type AnchoredPopoverHandle } from '../lib/anchored-popover.js';
import { type AchievementDisplay } from '../lib/achievement-catalog.js';
import { t } from '../lib/i18n/index.js';
import { ICON_GOLD, ICON_SHARD, achievementMedalGlyph } from './meta-icons.js';

/**
 * Подпись класса редкости (для детали — «частая / редкая / очень редкая»). i18n,
 * резолвится `t()` per-render (locale-реактивность). Тексты ачивок (имя/описание) —
 * творческий каталог-контент, остаются в achievement-catalog (НЕ локализуются тут).
 */
function rarityLabel(rarity: AchievementRarity): string {
  const key =
    rarity === 'common'
      ? 'game.achievementDetail.rarityCommon'
      : rarity === 'rare'
        ? 'game.achievementDetail.rarityRare'
        : 'game.achievementDetail.rarityVeryRare';
  return t(key);
}

/** Первая буква имени ачивки — на медальон-плейсхолдер (как в гриде значков). */
function firstLetter(name: string): string {
  return (name.trim()[0] ?? '?').toUpperCase();
}

/**
 * Не дать «логическим группам» в скобках вида «(10 / 100 / 1000)» рваться на строки
 * (owner-directive 2026-06-21: «логические группы без переносов, (1\5\15) одной строкой»):
 * заменяем пробелы ВНУТРИ скобок на NBSP — группа порогов держится одной строкой, остальной
 * текст описания переносится как обычно. textContent-safe (NBSP — обычный символ).
 */
function keepGroupsOnOneLine(text: string): string {
  return text.replace(/\(([^)]*)\)/g, (_m, inner: string) => `(${inner.replace(/ /g, ' ')})`);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (ch) =>
    ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : '&quot;',
  );
}

export interface AchievementDetailHandle {
  /**
   * Открыть/перенести/закрыть деталь ачивки у значка-триггера. Тот же значок повторно →
   * закрыть (тумблер, owner-decision 3); другой значок → перенести попап к нему.
   *
   * @param trigger значок-кнопка, у которого якорится попап.
   * @param def     отображаемая запись ачивки (имя/описание/тир).
   * @param isUnlocked получена ли (server-truth) — задаёт цвет рамки/галочку.
   */
  toggle(trigger: HTMLElement, def: AchievementDisplay, isUnlocked: boolean): void;
  /** Снять попап (закрыть + удалить из DOM). */
  destroy(): void;
}

/**
 * Создать единый анкер-попап детали ачивки. Один экземпляр на грид (значки делят его —
 * тап переносит/переоткрывает; ствол `anchored-popover` держит «один открытый за раз»).
 */
export interface AchievementDetailOptions {
  /**
   * Поставщик ГРАНИЦЫ облака (owner-directive 2026-06-21): прямоугольник, за который облако
   * НЕ выезжает (напр. таблица Профиля). Прокидывается в ствол anchored-popover. null/нет —
   * клампим к вьюпорту (badge-grid вне Профиля).
   */
  readonly getBoundary?: () => DOMRect | null;
}

export function createAchievementDetail(opts: AchievementDetailOptions = {}): AchievementDetailHandle {
  // Облако-описание (owner-directive 2026-06-21): БЕЗ кнопки закрытия (showCloseButton:false),
  // БЕЗ прокрутки; контент — вертикальный стек [картинка-медальон → название → описание →
  // награда]; значок+облако визуально объединены (подсветка значка [aria-expanded] + контур
  // облака/хвостик, achievement-detail.css). Анкер/флип/тумблер/оверлей + ГРАНИЦА — ствол
  // anchored-popover (getBoundary прокидывается от потребителя — деталь не знает про таблицу).
  const popover: AnchoredPopoverHandle = createAnchoredPopover({
    dialogClassName: 'badge-detail-dialog',
    showCloseButton: false,
    getBoundary: opts.getBoundary,
  });

  // ВЕСЬ контент — в body вертикальным стеком (header ствола скрыт CSS, т.к. крестика нет
  // и titleSlot не используем — порядок строго: картинка СВЕРХУ, затем имя).
  const medalEl = document.createElement('div');
  medalEl.className = 'badge-detail-medal';
  medalEl.setAttribute('aria-hidden', 'true');
  const titleEl = document.createElement('h3');
  titleEl.className = 'badge-detail-title';
  const howEl = document.createElement('p');
  howEl.className = 'badge-detail-how';
  const rewardEl = document.createElement('div');
  rewardEl.className = 'badge-detail-reward';
  popover.body.append(medalEl, titleEl, howEl, rewardEl);

  function render(def: AchievementDisplay, isUnlocked: boolean): void {
    // Скрытая-сюрприз (spec §3 / AC-7): до разблокировки критерий маскирован «???»
    // (имя/описание не раскрываются — «вскрылось — приятно»). После — раскрывается.
    const masked = def.hidden && !isUnlocked;
    const shownName = masked ? '???' : def.name;
    const shownHow = masked
      ? t('game.achievementDetail.hidden')
      : keepGroupsOnOneLine(def.description);
    // Картинка ачивки = медальон-глиф (общий примитив) + первая буква имени (как в гриде).
    medalEl.innerHTML = `${achievementMedalGlyph('badge-detail-medal-svg')}<span class="badge-detail-medal-letter">${escapeHtml(masked ? '?' : firstLetter(def.name))}</span>`;
    titleEl.textContent = shownName;
    // Описание ачивки («как получить») — основной текст облака. Группы порогов «(10 / 100 /
    // 1000)» держим одной строкой (NBSP) — owner «логические группы без переносов».
    howEl.textContent = shownHow;

    // Награда — компактные иконки (ICON_GOLD/ICON_SHARD из meta-icons; размер капнут CSS) +
    // класс редкости. Owner: «не цена, а награда» — что даёт ачивка за получение.
    const reward: { gold: number; shards: number } = ACH_REWARD[def.rarity];
    const parts: string[] = [];
    if (reward.gold > 0) {
      parts.push(`<span class="badge-detail-reward-row">${ICON_GOLD}<span>${reward.gold}</span></span>`);
    }
    if (reward.shards > 0) {
      parts.push(`<span class="badge-detail-reward-row">${ICON_SHARD}<span>${reward.shards}</span></span>`);
    }
    const rewardLabel = escapeHtml(t('game.achievementDetail.rewardLabel'));
    rewardEl.innerHTML = parts.length
      ? `<span class="badge-detail-reward-label">${rewardLabel}</span>${parts.join('')}<span class="badge-detail-tier"></span>`
      : `<span class="badge-detail-reward-label">${rewardLabel}</span><span class="badge-detail-reward-row">${escapeHtml(t('game.achievementDetail.rewardBadgeOnly'))}</span><span class="badge-detail-tier"></span>`;
    rewardEl.querySelector('.badge-detail-tier')!.textContent = rarityLabel(def.rarity);

    // Статус получено/не-получено — цветом рамки + галочкой (модификатор на dialog).
    popover.dialog.classList.toggle('badge-detail-dialog--on', isUnlocked);
    popover.dialog.classList.toggle('badge-detail-dialog--off', !isUnlocked);
    popover.dialog.setAttribute(
      'aria-label',
      t('game.achievementDetail.statusAria', {
        name: shownName,
        status: isUnlocked
          ? t('game.achievementDetail.statusUnlocked')
          : t('game.achievementDetail.statusLocked'),
      }),
    );
  }

  /**
   * Подогнать ширину облака ПОД САМУЮ ШИРОКУЮ СТРОКУ контента + var(--m) (owner-directive
   * 2026-06-22: «рамка прижимается к максимально выступающему тексту»). Описание переносится в
   * пределах CSS-капа (max-width), затем коробку сжимаем до самой длинной фактической строки,
   * чтобы справа/слева не оставалось пустоты сверх var(--m). Замер строк — Range.getClientRects()
   * (по строке-rect), плюс ширина одно-строчных награды/медальона. Ствол потом репозиционирует
   * (rAF) по новому offsetWidth. Текст центрирован → короткие строки стоят по центру (норма).
   */
  function hugToWidestLine(): void {
    const dlg = popover.dialog;
    if (!dlg.open) return;
    dlg.style.width = ''; // вернуть к CSS (max-content в пределах max-width) → перенос строк
    const rootM = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--m'));
    const m = Number.isFinite(rootM) ? rootM : 8;
    let maxLine = 0;
    for (const el of [titleEl, howEl]) {
      const range = document.createRange();
      range.selectNodeContents(el);
      const rects = range.getClientRects();
      for (let i = 0; i < rects.length; i++) maxLine = Math.max(maxLine, rects[i].width);
    }
    // Награда и медальон — одно-строчные: берём их фактическую ширину контента.
    maxLine = Math.max(maxLine, rewardEl.getBoundingClientRect().width, medalEl.getBoundingClientRect().width);
    // Коробка = самая длинная строка + padding var(--m) с боков + рамка 1px с боков.
    dlg.style.width = `${Math.ceil(maxLine) + 2 * m + 2}px`;
  }

  return {
    toggle(trigger, def, isUnlocked): void {
      // Если переоткрываем у того же триггера — ствол сам закроет (тумблер). Контент
      // перерисовываем заранее, чтобы при переносе к другому значку был свежим.
      const reopeningSame = popover.currentTrigger() === trigger && popover.dialog.open;
      if (!reopeningSame) render(def, isUnlocked);
      popover.toggle(trigger);
      // После открытия (showModal+layout) — подгон ширины под самую длинную строку; ствол
      // репозиционирует по новому размеру в своём rAF. Закрытие (тумблер) → dlg.open=false → skip.
      hugToWidestLine();
    },
    destroy(): void {
      popover.destroy();
    },
  };
}
